import { useCallback, useMemo } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { dateFromForPreset } from '../components/FeedFilterPanel';

export const PAGE_SIZE = 10;

// Only recognitions can match these filter sections, so nominations are
// excluded from the feed while any of them is active (AND across sections).
export const hasRecognitionOnlyFilters = (f) =>
  f.typeIds.length > 0 || f.teamIds.length > 0 || f.points.length === 1;

const sharedParams = (f) => {
  const params = {};
  if (f.users.length) params.user_ids = f.users.map((u) => u.value);
  if (f.involvement.length) params.involvement = f.involvement;
  const dateFrom = dateFromForPreset(f.datePreset);
  if (dateFrom) params.date_from = dateFrom;
  return params;
};

const recognitionParams = (sortBy, f) => {
  const params = { sort_by: sortBy, ...sharedParams(f) };
  if (f.typeIds.length) params.recognition_type_ids = f.typeIds;
  if (f.teamIds.length) params.team_ids = f.teamIds;
  if (f.points.length === 1) params.has_points = f.points[0] === 'with';
  return params;
};

const itemPoints = (item) =>
  item.item_type === 'nomination'
    ? Number(item.points_awarded_recipient ?? item.campaign?.award_points ?? 0)
    : Number(item.points_amount ?? 0);

const itemTime = (item) => new Date(item.timestamp || item.date || 0).getTime();

// Mirrors the server ORDER BY so the two streams can be merged into one list.
export const comparatorFor = (sortBy) => {
  if (sortBy === 'oldest') return (a, b) => itemTime(a) - itemTime(b);
  if (sortBy === 'highest_points') {
    return (a, b) => itemPoints(b) - itemPoints(a) || itemTime(b) - itemTime(a);
  }
  return (a, b) => itemTime(b) - itemTime(a);
};

const mergeStreams = (a, b, compare) => {
  const merged = [];
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    merged.push(compare(a[i], b[j]) <= 0 ? a[i++] : b[j++]);
  }
  while (i < a.length) merged.push(a[i++]);
  while (j < b.length) merged.push(b[j++]);
  return merged;
};

/**
 * Merges the two loaded streams and drops the tail that is not yet safe to show.
 *
 * A stream with more pages tells us nothing about what comes after its last
 * loaded item, so the merged list is only complete up to whichever unfinished
 * stream ran out first. Rendering past that point would let the next page
 * insert cards above content the user has already scrolled past.
 */
export const mergeWithFrontier = (recognitions, nominations, compare, more) => {
  const merged = mergeStreams(recognitions, nominations, compare);

  const tails = [];
  if (more.recognitions && recognitions.length) {
    tails.push(recognitions[recognitions.length - 1]);
  }
  if (more.nominations && nominations.length) {
    tails.push(nominations[nominations.length - 1]);
  }
  if (tails.length === 0) return merged;

  const [frontier] = tails.sort(compare);
  const cut = merged.findIndex((item) => compare(item, frontier) >= 0);
  return cut === -1 ? merged : merged.slice(0, cut);
};

const fetchPage = async (url, params, pageParam, itemType) => {
  const { data } = await api.get(url, {
    params: { ...params, skip: pageParam, limit: PAGE_SIZE },
  });
  const items = Array.isArray(data) ? data : data?.data || [];
  return items.map((item) => ({ ...item, item_type: itemType }));
};

const nextPageParam = (lastPage, allPages) =>
  lastPage.length === PAGE_SIZE
    ? allPages.reduce((total, page) => total + page.length, 0)
    : undefined;

/**
 * Pages the recognition and nomination feeds independently and merges them
 * into one ordered list.
 *
 * Both endpoints filter and sort server-side, so a filter covers the whole
 * history rather than the pages already loaded. Because they are separate
 * streams, the merged list is cut at the earlier of the two stream tails: past
 * that point an unloaded item from the other stream could still belong, and
 * showing it early would make the next page insert cards above content the
 * user has already scrolled past.
 */
export default function useFeedPages(sortBy, filters) {
  const queryClient = useQueryClient();
  const skipNominations = hasRecognitionOnlyFilters(filters);

  const recognitionsQuery = useInfiniteQuery({
    queryKey: ['feed', 'recognitions', sortBy, filters],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      fetchPage(
        '/api/v1/recognitions/feed',
        recognitionParams(sortBy, filters),
        pageParam,
        'recognition'
      ),
    getNextPageParam: nextPageParam,
  });

  const nominationsQuery = useInfiniteQuery({
    queryKey: ['feed', 'nominations', sortBy, filters],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      fetchPage(
        '/api/v1/nominations/feed',
        { sort_by: sortBy, ...sharedParams(filters) },
        pageParam,
        'nomination'
      ),
    getNextPageParam: nextPageParam,
    enabled: !skipNominations,
  });

  const recognitionPages = recognitionsQuery.data;
  const nominationPages = skipNominations ? undefined : nominationsQuery.data;

  const recognitions = useMemo(
    () => recognitionPages?.pages.flat() ?? [],
    [recognitionPages]
  );
  const nominations = useMemo(
    () => nominationPages?.pages.flat() ?? [],
    [nominationPages]
  );

  const items = useMemo(
    () =>
      mergeWithFrontier(recognitions, nominations, comparatorFor(sortBy), {
        recognitions: recognitionsQuery.hasNextPage,
        nominations: !skipNominations && nominationsQuery.hasNextPage,
      }),
    [
      recognitions,
      nominations,
      sortBy,
      skipNominations,
      recognitionsQuery.hasNextPage,
      nominationsQuery.hasNextPage,
    ]
  );

  const hasMore = Boolean(
    recognitionsQuery.hasNextPage ||
      (!skipNominations && nominationsQuery.hasNextPage)
  );

  const loadMore = useCallback(() => {
    const compare = comparatorFor(sortBy);
    const candidates = [];
    if (recognitionsQuery.hasNextPage && !recognitionsQuery.isFetchingNextPage) {
      candidates.push([recognitions[recognitions.length - 1], recognitionsQuery]);
    }
    if (
      !skipNominations &&
      nominationsQuery.hasNextPage &&
      !nominationsQuery.isFetchingNextPage
    ) {
      candidates.push([nominations[nominations.length - 1], nominationsQuery]);
    }
    if (candidates.length === 0) return;

    // Extend the stream that is holding the frontier back; a stream with no
    // items yet has nothing to compare, so it always goes first.
    candidates.sort(([a], [b]) => {
      if (!a) return -1;
      if (!b) return 1;
      return compare(a, b);
    });
    candidates[0][1].fetchNextPage();
  }, [
    sortBy,
    skipNominations,
    recognitions,
    nominations,
    recognitionsQuery,
    nominationsQuery,
  ]);

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['feed'] });
  }, [queryClient]);

  // Optimistic edits rewrite the cached pages so the feed does not have to
  // refetch everything the user has already scrolled through.
  const patchStream = useCallback(
    (stream, updater) => {
      queryClient.setQueriesData(
        { queryKey: ['feed', stream] },
        (cached) =>
          cached && { ...cached, pages: cached.pages.map(updater) }
      );
    },
    [queryClient]
  );

  const removeRecognitions = useCallback(
    (ids = []) => {
      const removed = new Set(ids);
      patchStream('recognitions', (page) =>
        page.filter((r) => !removed.has(r.id))
      );
    },
    [patchStream]
  );

  const removeNomination = useCallback(
    (id) => {
      patchStream('nominations', (page) => page.filter((n) => n.id !== id));
    },
    [patchStream]
  );

  const updateNomination = useCallback(
    (updated) => {
      patchStream('nominations', (page) =>
        page.map((n) =>
          n.id === updated.id
            ? { ...n, ...updated, item_type: 'nomination' }
            : n
        )
      );
    },
    [patchStream]
  );

  const isFetching =
    recognitionsQuery.isFetching ||
    (!skipNominations && nominationsQuery.isFetching);

  return {
    items,
    isLoading:
      recognitionsQuery.isPending ||
      (!skipNominations && nominationsQuery.isPending),
    isFetching,
    isFetchingMore:
      recognitionsQuery.isFetchingNextPage ||
      nominationsQuery.isFetchingNextPage,
    error: recognitionsQuery.error?.message || null,
    hasMore,
    loadMore,
    refresh,
    removeRecognitions,
    removeNomination,
    updateNomination,
  };
}
